import { useEffect, useRef, FC, useState } from "react";
import {
  Editor as MonacoReactEditor,
  DiffEditor as MonacoDiffEditor,
  OnMount,
  useMonaco,
} from "@monaco-editor/react";
import type * as monaco from "monaco-editor/esm/vs/editor/editor.api";
import { useSettingsStore } from "@/stores/settings";
import { generateMonacoThemeData } from "@/lib/monaco-theme";
import { useDebounce } from "@/hooks/use-debounce";
import { useIsMobile } from "@/hooks/use-is-mobile";
import { Loader2, CheckCircle2, AlertCircle as AlertCircleIcon, Circle } from "lucide-react";
import { ValidationStatus } from "@/core/state-shape";

// --- Helpers (not exported) ---

const getLanguageFromPath = (filePath:   string): string => {
  const extension = filePath.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "js":
      return "javascript";
    case "jsx":
      return "javascript";
    case "ts":
      return "typescript";
    case "tsx":
      return "typescript";
    case "py":
      return "python";
    case "css":
      return "css";
    case "html":
      return "html";
    case "json":
      return "json";
    case "md":
      return "markdown";
    case "java":
      return "java";
    case "cs":
      return "csharp";
    case "cpp":
    case "h":
      return "cpp";
    case "sh":
      return "shell";
    case "go":
      return "go";
    case "rs":
      return "rust";
    default:
      return "plaintext";
  }
};

const useMonacoConfig = () => {
  const monaco = useMonaco();
  const appTheme = useSettingsStore((state) => state.appearance.theme);
  const isMobile = useIsMobile();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (monaco) {
      const isDark =
        appTheme === "dark" ||
        (appTheme === "system" &&
          window.matchMedia("(prefers-color-scheme: dark)").matches);
      const themeData = generateMonacoThemeData(isDark, isMobile);
      monaco.editor.defineTheme("shadcn-theme", themeData);

      monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
        jsx: monaco.languages.typescript.JsxEmit.ReactJSX,
        allowNonTsExtensions: true,
      });

      monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        jsx: monaco.languages.typescript.JsxEmit.ReactJSX,
      });

      monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: true,
        noSyntaxValidation: false,
      });

      setLoaded(true);
    }
  }, [appTheme, monaco, isMobile]);

  return loaded;
};

const ValidationIndicator: FC<{
  status?: ValidationStatus;
  onClick?: () => void;
}> = ({ status, onClick }) => {
  if (!status) {
    return <Circle className="h-4 w-4 text-muted-foreground"  />;
  }
  if (status.status === 'pending') {
    return <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />;
  }
  if (status.status === 'valid') {
    return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  }
  if (status.status === 'invalid') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="p-0 border-0 bg-transparent cursor-pointer rounded-full focus:outline-none focus:ring-2 focus:ring-destructive focus:ring-offset-1 focus:ring-offset-background"
        aria-label={`Scroll to syntax error: ${status.error?.message}`}
      >
        <AlertCircleIcon className="h-4 w-4 text-destructive" />
      </button>
    );
  }
  return null;
};

type CoverageData = {
  path: string;
  summary: any;
  line_hits: Record<string, number>;
};


interface EditorProps {
  path: string;
  value: string;
  language?: string;
  onChange?: (value: string | undefined) => void;
  onMount?: OnMount;
  readOnly?: boolean;
  height?: string | number;
  onValidate?: (value: string) => void;
  validationStatus?: ValidationStatus;
  coverage?: CoverageData;
  testFailures?: { line: number; message: string }[];
}

export const Editor: React.FC<EditorProps> = ({
  path,
  value,
  language,
  onChange,
  onMount,
  readOnly = false,
  height = "100%",
  onValidate,
  validationStatus,
  coverage,
  testFailures,
}) => {
  const loaded = useMonacoConfig();
  const isMobile = useIsMobile();
  const monaco = useMonaco();
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);
  const decorationsRef = useRef<string[]>([]);
  const testFailureDecorationsRef = useRef<string[]>([]);
  const [cursorPosition, setCursorPosition] = useState({ lineNumber: 1, column: 1 });
  const [isMounted, setIsMounted] = useState(false);

  const debouncedValue = useDebounce(value, 500);

  useEffect(() => {
    const styleId = 'monaco-coverage-styles';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .coverage-covered { background-color: rgba(60, 179, 113, 0.15); }
      .coverage-uncovered { background-color: rgba(255, 99, 71, 0.15); }
      .coverage-glyph-covered { background: mediumseagreen; }
      .coverage-glyph-uncovered { background: tomato; }
      .test-failure-line { background-color: rgba(239, 68, 68, 0.15); }
      .test-failure-glyph { background: #ef4444; }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    if (onValidate && !readOnly) {
      onValidate(debouncedValue);
    }
  }, [debouncedValue, onValidate, readOnly]);

  const handleEditorMount: OnMount = (editor, monacoInstance) => {
    editorRef.current = editor;
    setIsMounted(true);
    if (onMount) {
      onMount(editor, monacoInstance);
    }
    editor.onDidChangeCursorPosition(e => {
        if (e.position) setCursorPosition(e.position);
    });
  };

  useEffect(() => {
    if (!isMounted) return;

    const editor = editorRef.current;
    if (!editor || !monaco) return;

    const isFullCoverage = coverage?.summary?.lines?.pct === 100;

    if (coverage?.line_hits && !isFullCoverage) {
      const newDecorations: monaco.editor.IModelDeltaDecoration[] = [];
      for (const lineStr in coverage.line_hits) {
        const lineNumber = parseInt(lineStr, 10);
        if (isNaN(lineNumber)) continue;

        const hits = coverage.line_hits[lineStr];
        const className = hits > 0 ? 'coverage-covered' : 'coverage-uncovered';
        const glyphClassName = hits > 0 ? 'coverage-glyph-covered' : 'coverage-glyph-uncovered';

        newDecorations.push({
          range: new monaco.Range(lineNumber, 1, lineNumber, 1),
          options: {
            isWholeLine: true,
            className,
            glyphMarginClassName: glyphClassName,
          }
        });
      }
      decorationsRef.current = editor.deltaDecorations(decorationsRef.current, newDecorations);
    } else {
      decorationsRef.current = editor.deltaDecorations(decorationsRef.current, []);
    }
  }, [coverage, monaco, isMounted]);

  useEffect(() => {
    if (!isMounted) return;

    const editor = editorRef.current;
    if (!editor || !monaco) return;

    if (testFailures && testFailures.length > 0) {
      const newDecorations: monaco.editor.IModelDeltaDecoration[] = testFailures.map(failure => ({
        range: new monaco.Range(failure.line, 1, failure.line, 1),
        options: {
          isWholeLine: true,
          className: 'test-failure-line',
          glyphMarginClassName: 'test-failure-glyph',
          glyphMarginHoverMessage: { value: `Test Failure: ${failure.message}` },
        }
      }));
      testFailureDecorationsRef.current = editor.deltaDecorations(testFailureDecorationsRef.current, newDecorations);
    } else {
      testFailureDecorationsRef.current = editor.deltaDecorations(testFailureDecorationsRef.current, []);
    }

  }, [testFailures, monaco, isMounted]);

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !monaco) return;
    const model = editor.getModel();
    if (!model) return;

    if (validationStatus?.status === "invalid" && validationStatus.error) {
      const { line, column, message } = validationStatus.error;
      monaco.editor.setModelMarkers(model, "ast-validator", [
        {
          startLineNumber: line,
          startColumn: column ?? 1,
          endLineNumber: line,
          endColumn: model.getLineMaxColumn(line),
          message: message,
          severity: monaco.MarkerSeverity.Error,
        },
      ]);
    } else {
      monaco.editor.setModelMarkers(model, "ast-validator", []);
    }
  }, [validationStatus, monaco]);

  const handleScrollToError = () => {
    const editor = editorRef.current;
    if (!editor || !monaco) return;
    if (validationStatus?.status === "invalid" && validationStatus.error) {
      editor.revealLineInCenter(
        validationStatus.error.line,
        monaco.editor.ScrollType.Smooth
      );
      editor.setPosition({
        lineNumber: validationStatus.error.line,
        column: validationStatus.error.column ?? 1,
      });
      editor.focus();
    }
  };

  if (!loaded) {
    return null;
  };

  return (
    <div className="flex flex-col w-full" style={{ height }}>
      <div className="flex-1 min-h-0">
        <MonacoReactEditor
          height="100%"
          path={path}
          language={language || getLanguageFromPath(path)}
          value={value}
          theme="shadcn-theme"
          onMount={handleEditorMount}
          onChange={onChange}
          options={{
            readOnly,
            automaticLayout: true,
            scrollBeyondLastLine: false,
            minimap: { enabled: false },
            fontSize: isMobile ? 10 : undefined,
            lineNumbersMinChars: isMobile ? 3 : undefined,
            folding: !isMobile,
            glyphMargin: true,
          }}
        />
      </div>
      <div className="flex items-center justify-between h-6 px-2 text-xs bg-background border-t text-muted-foreground shrink-0">
        <span>Ln {cursorPosition.lineNumber}, Col {cursorPosition.column}</span>
        {!readOnly && (
          <ValidationIndicator status={validationStatus} onClick={handleScrollToError} />
        )}
      </div>
    </div>
  );
};

// --- DiffEditor component ---

interface DiffEditorProps {
  filePath: string;
  original: string;
  modified: string;
  onModifiedChange?: (value: string) => void;
  onValidate?: (value: string) => void;
  validationStatus?: ValidationStatus;
  isSideBySide?: boolean;
  readOnly?: boolean;
  onReady?: (editor: monaco.editor.IStandaloneDiffEditor | null) => void;
  height?: string | number;
}

export const DiffEditor: React.FC<DiffEditorProps> = ({
  filePath,
  original,
  modified,
  onModifiedChange,
  onValidate,
  validationStatus,
  isSideBySide = true,
  readOnly = false,
  onReady,
  height = "100%",
}) => {
  const loaded = useMonacoConfig();
  const isMobile = useIsMobile();
  const monaco = useMonaco();
  const editorRef = useRef<monaco.editor.IStandaloneDiffEditor | null>(null);
  const [cursorPosition, setCursorPosition] = useState({ lineNumber: 1, column: 1 });

  const debouncedModifiedContent = useDebounce(modified, 500);

  useEffect(() => {
    if (onValidate && !readOnly) {
      onValidate(debouncedModifiedContent);
    }
  }, [debouncedModifiedContent, onValidate, readOnly]);

  useEffect(() => {
    const editor = editorRef.current?.getModifiedEditor();
    if (!editor || !monaco) return;
    const model = editor.getModel();
    if (!model) return;

    if (validationStatus?.status === "invalid" && validationStatus.error) {
        const { line, column, message } = validationStatus.error;
        monaco.editor.setModelMarkers(model, 'ast-validator', [{
            startLineNumber: line,
            startColumn: column ?? 1,
            endLineNumber: line,
            endColumn: model.getLineMaxColumn(line),
            message: message,
            severity: monaco.MarkerSeverity.Error,
        }]);
    } else {
      monaco.editor.setModelMarkers(model, "ast-validator", []);
    }
  }, [validationStatus, monaco]);

  const handleEditorMount: any = (editor: monaco.editor.IStandaloneDiffEditor) => {
    editorRef.current = editor as monaco.editor.IStandaloneDiffEditor;
    if (onReady) onReady(editor as monaco.editor.IStandaloneDiffEditor);
    
    const modifiedEditor = (editor as monaco.editor.IStandaloneDiffEditor).getModifiedEditor();
    modifiedEditor.onDidChangeModelContent(() => {
      if (onModifiedChange) {
        onModifiedChange(modifiedEditor.getValue());
      }
    });
    modifiedEditor.onDidChangeCursorPosition(e => {
        if (e.position) setCursorPosition(e.position);
    });
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      if (onReady) onReady(null);
    };
  }, [onReady]);

  const handleScrollToError = () => {
    const editor = editorRef.current;
    if (!editor || !monaco) return;

    if (validationStatus?.status === "invalid" && validationStatus.error) {
      const modifiedEditor = editor.getModifiedEditor();
      modifiedEditor.revealLineInCenter(
        validationStatus.error.line,
        monaco.editor.ScrollType.Smooth
      );
      modifiedEditor.setPosition({
        lineNumber: validationStatus.error.line,
        column: validationStatus.error.column ?? 1,
      });
      modifiedEditor.focus();
    }
  };

  if (!loaded) {
    return null;
  };

  return (
    <div className="flex flex-col w-full" style={{ height }}>
      <div className="flex-1 min-h-0">
        <MonacoDiffEditor
          theme="shadcn-theme"
          height="100%"
          language={getLanguageFromPath(filePath)}
          original={original}
          modified={modified}
          onMount={handleEditorMount}
          options={{
            renderSideBySide: isSideBySide,
            readOnly: readOnly,
            automaticLayout: true,
            scrollBeyondLastLine: false,
            minimap: { enabled: false },
            wordWrap: readOnly ? "on" : "off",
            fontSize: isMobile ? 10 : undefined,
            lineNumbersMinChars: isMobile ? 3 : undefined,
            folding: !isMobile,
          }}
        />
      </div>
      <div className="flex items-center justify-end h-6 px-2 text-xs bg-background border-t text-muted-foreground shrink-0 gap-4">
        {!readOnly && <span>Ln {cursorPosition.lineNumber}, Col {cursorPosition.column}</span>}
        {!readOnly && (
          <ValidationIndicator status={validationStatus} onClick={handleScrollToError} />
        )}
      </div>
    </div>
  );
};